/**
 * Who can reach one project (CLAUDE.md 11.3, 11.5, 2.2).
 *
 * Distinct from `ShareDialog`, which mints read-only links for people with no
 * account. This is the *inside* of the firm: visibility, explicit grants, and moving
 * the project to another workspace.
 *
 * Three statements here are load-bearing and must not be softened into marketing:
 *
 * 1. **Private is not private from your administrators.** A workspace `admin` or
 *    `moderator` retains read access to every project for governance (11.3). Anyone
 *    choosing "private" is entitled to know that before they choose it.
 * 2. **`reviewer` is the role that makes the check/approve workflow possible** (2.2):
 *    reads everything, signs off, cannot touch the inputs. Left unexplained it looks
 *    like a weaker editor, and the independent-checker requirement quietly stops
 *    being used.
 * 3. **A transfer drops grants held by people who are not members of the
 *    destination**, and the response names them. Showing that list is the whole
 *    reason the endpoint returns it (11.5) -- a colleague discovering their own loss
 *    of access is not an acceptable substitute.
 */
import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowRightLeft, UserPlus, UserMinus } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { ApiError } from '@/api/client'
import {
  useAddProjectMember,
  useProjectMembers,
  useRemoveProjectMember,
  useSetVisibility,
  useTransferProject,
  useUpdateProjectMember,
} from '@/api/projectMembers'
import { useWorkspace, useWorkspaceMembers } from '@/api/workspaces'
import type { Project, ProjectRole, ProjectVisibility } from '@/api/types'
import { useAuth } from '@/auth/AuthProvider'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { SelectField } from '@/components/ui/select'
import { useToast } from '@/components/ui/toast'

export const PROJECT_ROLES: ProjectRole[] = ['owner', 'editor', 'reviewer', 'viewer']

export function ProjectSharingDialog({
  project,
  open,
  onOpenChange,
}: {
  project: Project
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const { t } = useTranslation()
  const { data: workspace } = useWorkspace()

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>{t('sharing.title')}</DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Visibility only means something where there are colleagues to be visible
              to. A personal workspace forces `private` and hides the control (11.3). */}
          {workspace && !workspace.is_personal && (
            <VisibilitySection project={project} enabled={open} />
          )}

          <PeopleSection project={project} enabled={open} />

          <TransferSection project={project} onOpenChange={onOpenChange} />
        </div>
      </DialogContent>
    </Dialog>
  )
}

function VisibilitySection({ project, enabled }: { project: Project; enabled: boolean }) {
  const { t } = useTranslation()
  const { toast } = useToast()
  const setVisibility = useSetVisibility(project.id)

  return (
    <section className="space-y-2">
      <h3 className="text-sm font-semibold">{t('sharing.visibility')}</h3>
      <SelectField
        label={t('sharing.visibility')}
        labelHidden
        disabled={!enabled || setVisibility.isPending}
        value={project.visibility}
        onChange={(event) =>
          setVisibility.mutate(event.target.value as ProjectVisibility, {
            onSuccess: () => toast({ title: t('common.saved'), variant: 'success' }),
            onError: (caught) =>
              toast({
                title: t('common.error'),
                description: caught instanceof ApiError ? caught.localised : undefined,
                variant: 'error',
              }),
          })
        }
      >
        <option value="private">{t('workspace.visibility.private')}</option>
        <option value="workspace">{t('workspace.visibility.workspace')}</option>
      </SelectField>
      {/* Said plainly rather than buried: see the file header. */}
      <p className="text-xs text-text-muted">{t('sharing.visibility_governance')}</p>
    </section>
  )
}

function PeopleSection({ project, enabled }: { project: Project; enabled: boolean }) {
  const { t } = useTranslation()
  const { workspaceId } = useAuth()
  const { toast } = useToast()

  const { data: people } = useProjectMembers(project.id, enabled)
  const { data: workspace } = useWorkspace()
  const { data: workspaceMembers } = useWorkspaceMembers(
    workspaceId,
    enabled && workspace !== undefined && !workspace.is_personal,
  )

  const updateMember = useUpdateProjectMember(project.id)
  const removeMember = useRemoveProjectMember(project.id)
  const addMember = useAddProjectMember(project.id)

  const [candidateId, setCandidateId] = useState('')
  const [candidateRole, setCandidateRole] = useState<ProjectRole>('editor')

  const granted = useMemo(() => {
    const ids = new Set<number>()
    if (people) {
      ids.add(people.owner.user_id)
      for (const member of people.members) ids.add(member.user_id)
    }
    return ids
  }, [people])

  const candidates = (workspaceMembers ?? []).filter((m) => !granted.has(m.user_id))

  const showError = (error: unknown) =>
    toast({
      title: t('common.error'),
      description: error instanceof ApiError ? error.localised : undefined,
      variant: 'error',
    })

  return (
    <section className="space-y-2 border-t border-border pt-4">
      <h3 className="text-sm font-semibold">{t('sharing.people')}</h3>
      <p className="text-xs text-text-muted">{t('sharing.people_hint')}</p>

      <ul className="space-y-2">
        {people && (
          <li className="flex flex-col gap-1 rounded-md border border-border p-3 sm:flex-row sm:items-center">
            <span className="min-w-0 flex-1 truncate text-sm font-medium">
              {people.owner.display_name || people.owner.username}
            </span>
            <span className="text-xs text-text-muted sm:w-52">{t('sharing.role.owner')}</span>
          </li>
        )}

        {(people?.members ?? []).map((member) => (
          <li
            key={member.user_id}
            className="flex flex-col gap-2 rounded-md border border-border p-3 sm:flex-row sm:items-start"
          >
            <span className="min-w-0 flex-1 truncate text-sm font-medium">
              {member.display_name || member.username}
            </span>

            <div className="sm:w-52">
              <SelectField
                label={`${t('members.role')} — ${member.display_name || member.username}`}
                labelHidden
                description={t(`sharing.role_hint.${member.role}`)}
                value={member.role}
                onChange={(event) =>
                  updateMember.mutate(
                    { userId: member.user_id, role: event.target.value as ProjectRole },
                    { onError: showError },
                  )
                }
              >
                {PROJECT_ROLES.map((role) => (
                  <option key={role} value={role}>
                    {t(`sharing.role.${role}`)}
                  </option>
                ))}
              </SelectField>
            </div>

            <Button
              size="sm"
              variant="ghost"
              aria-label={t('sharing.remove_person')}
              onClick={() => removeMember.mutate(member.user_id, { onError: showError })}
            >
              <UserMinus size={16} aria-hidden />
            </Button>
          </li>
        ))}
      </ul>

      {workspace && !workspace.is_personal && (
        <div className="space-y-2 pt-2">
          {candidates.length === 0 ? (
            <p className="text-xs text-text-muted">{t('sharing.no_candidates')}</p>
          ) : (
            <div className="grid gap-2 sm:grid-cols-[1fr_auto]">
              <div className="grid gap-2 sm:grid-cols-2">
                <SelectField
                  label={t('sharing.add_person')}
                  value={candidateId}
                  onChange={(event) => setCandidateId(event.target.value)}
                >
                  <option value="">{t('common.none')}</option>
                  {candidates.map((member) => (
                    <option key={member.user_id} value={String(member.user_id)}>
                      {member.display_name || member.username}
                    </option>
                  ))}
                </SelectField>

                <SelectField
                  label={t('members.role')}
                  description={t(`sharing.role_hint.${candidateRole}`)}
                  value={candidateRole}
                  onChange={(event) => setCandidateRole(event.target.value as ProjectRole)}
                >
                  {PROJECT_ROLES.map((role) => (
                    <option key={role} value={role}>
                      {t(`sharing.role.${role}`)}
                    </option>
                  ))}
                </SelectField>
              </div>

              <Button
                className="self-end"
                disabled={!candidateId}
                loading={addMember.isPending}
                onClick={() =>
                  addMember.mutate(
                    { user_id: Number(candidateId), role: candidateRole },
                    { onSuccess: () => setCandidateId(''), onError: showError },
                  )
                }
              >
                <UserPlus size={16} aria-hidden />
                {t('sharing.add')}
              </Button>
            </div>
          )}
        </div>
      )}
    </section>
  )
}

function TransferSection({
  project,
  onOpenChange,
}: {
  project: Project
  onOpenChange: (open: boolean) => void
}) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { workspaces, switchWorkspace } = useAuth()
  const { data: people } = useProjectMembers(project.id)
  const transfer = useTransferProject(project.id)

  const [targetId, setTargetId] = useState('')
  const [confirming, setConfirming] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [result, setResult] = useState<{ workspace: string; dropped: string[] } | null>(null)

  const targets = workspaces.filter((w) => w.id !== project.workspace_id)

  // Captured from the list held *before* the transfer: the ids come back after the
  // grants have already gone, so the names must be resolved from what we still have.
  const nameById = useMemo(() => {
    const map = new Map<number, string>()
    if (people) {
      for (const member of [people.owner, ...people.members]) {
        map.set(member.user_id, member.display_name || member.username)
      }
    }
    return map
  }, [people])

  const targetName = (id: number) => {
    const found = workspaces.find((w) => w.id === id)
    if (!found) return ''
    return found.is_personal ? t('workspace.personal') : found.name
  }

  if (result) {
    return (
      <section className="space-y-2 border-t border-border pt-4">
        <h3 className="text-sm font-semibold">{t('sharing.transfer')}</h3>
        <p className="text-sm">{t('sharing.transfer_done', { workspace: result.workspace })}</p>

        {result.dropped.length === 0 ? (
          <p className="text-xs text-text-muted">{t('sharing.transfer_kept')}</p>
        ) : (
          <div className="rounded-md border border-[var(--status-warn)] bg-[var(--status-warn-bg)] p-3">
            <p className="text-xs text-[var(--status-warn)]">{t('sharing.transfer_dropped')}</p>
            <ul className="mt-1 list-inside list-disc text-sm">
              {result.dropped.map((name) => (
                <li key={name}>{name}</li>
              ))}
            </ul>
          </div>
        )}

        <Button
          variant="primary"
          onClick={() => {
            const destination = Number(targetId)
            onOpenChange(false)
            // The project now lives elsewhere, and `X-Workspace-Id` still names the
            // old workspace -- leaving the page as it is would answer 404 on the next
            // refetch. Follow the project instead.
            switchWorkspace(destination)
            navigate(`/projects/${project.id}`)
          }}
        >
          {t('sharing.transfer_continue', { workspace: result.workspace })}
        </Button>
      </section>
    )
  }

  return (
    <section className="space-y-2 border-t border-border pt-4">
      <h3 className="text-sm font-semibold">{t('sharing.transfer')}</h3>
      <p className="text-xs text-text-muted">{t('sharing.transfer_hint')}</p>

      {targets.length === 0 ? (
        <p className="text-sm text-text-muted">{t('sharing.transfer_none')}</p>
      ) : (
        <div className="space-y-3">
          <SelectField
            label={t('sharing.transfer_target')}
            value={targetId}
            onChange={(event) => {
              setTargetId(event.target.value)
              setConfirming(false)
            }}
          >
            <option value="">{t('common.none')}</option>
            {targets.map((workspace) => (
              <option key={workspace.id} value={String(workspace.id)}>
                {workspace.is_personal ? t('workspace.personal') : workspace.name}
              </option>
            ))}
          </SelectField>

          {error && (
            <p
              role="alert"
              className="rounded-md bg-[var(--status-fail-bg)] p-3 text-sm text-[var(--status-fail)]"
            >
              {error}
            </p>
          )}

          {confirming ? (
            <div className="space-y-2 rounded-md border border-border p-3">
              <p className="text-sm">
                {t('sharing.transfer_confirm', { workspace: targetName(Number(targetId)) })}
              </p>
              <div className="flex flex-wrap gap-2">
                <Button onClick={() => setConfirming(false)}>{t('action.cancel')}</Button>
                <Button
                  variant="primary"
                  loading={transfer.isPending}
                  onClick={() => {
                    setError(null)
                    transfer.mutate(Number(targetId), {
                      onSuccess: (outcome) => {
                        setConfirming(false)
                        setResult({
                          workspace: targetName(Number(targetId)),
                          dropped: outcome.dropped_member_ids.map(
                            (id) => nameById.get(id) ?? String(id),
                          ),
                        })
                      },
                      onError: (caught) =>
                        setError(caught instanceof ApiError ? caught.localised : String(caught)),
                    })
                  }}
                >
                  {t('sharing.transfer_action')}
                </Button>
              </div>
            </div>
          ) : (
            <Button disabled={!targetId} onClick={() => setConfirming(true)}>
              <ArrowRightLeft size={16} aria-hidden />
              {t('sharing.transfer_action')}
            </Button>
          )}
        </div>
      )}
    </section>
  )
}
